import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, VStack, HStack, Text, Spinner, Badge, Flex, IconButton, Input,
    Table, Thead, Tbody, Tr, Th, Td, useToast, Switch, Button,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

// Default to the current UTC month as 'YYYY-MM'.
function currentMonth() {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * VisionUsageSection — platform-admin view of AI photo-counting (Vision) usage.
 *
 * AI item counting is an opt-in paid add-on (default off). This panel lets the
 * platform admin flip a tenant's AI on/off, edit the per-order rate, and see
 * this month's billable orders + resulting charge to fold into the manual
 * monthly invoice.
 */
export default function VisionUsageSection({ platformKey, onAuthError }) {
    const [month, setMonth] = useState(currentMonth());
    const [tenants, setTenants] = useState([]);
    const [grandOrders, setGrandOrders] = useState(0);
    const [grandCharge, setGrandCharge] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [rateDrafts, setRateDrafts] = useState({}); // laundryId -> string
    const toast = useToast();

    const headers = { 'x-platform-key': platformKey };

    const fetchUsage = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/platform/vision-usage`, {
                params: { month },
                headers,
            });
            const data = res.data || {};
            setTenants(data.tenants || []);
            setGrandOrders(data.grandOrders || 0);
            setGrandCharge(data.grandCharge || 0);
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                onAuthError && onAuthError();
            } else {
                toast({ title: 'Failed to load AI usage', status: 'error', duration: 3000 });
            }
        } finally {
            setLoading(false);
        }
    }, [month, platformKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (platformKey) fetchUsage(); }, [fetchUsage, platformKey]);

    const saveSettings = async (laundryId, { aiEnabled, orderRate }) => {
        setSavingId(laundryId);
        try {
            const body = { laundryId };
            if (aiEnabled !== undefined) body.aiEnabled = aiEnabled;
            if (orderRate !== undefined) body.orderRate = orderRate;
            const res = await axios.post(`${API_URL}/api/platform/vision-settings`, body, { headers });
            const data = res.data || {};
            if (data.status !== 'success') {
                toast({ title: data.message || 'Update failed', status: 'error', duration: 3000 });
                return;
            }
            // Merge the confirmed values back into the row.
            setTenants((prev) => prev.map((t) =>
                t.laundryId === laundryId
                    ? { ...t, aiEnabled: data.aiEnabled, orderRate: data.orderRate }
                    : t
            ));
            setRateDrafts((prev) => {
                const next = { ...prev };
                delete next[laundryId];
                return next;
            });
            // Recompute charge for the changed row so the grand total stays honest.
            fetchUsage();
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                onAuthError && onAuthError();
            } else {
                toast({ title: 'Update failed', status: 'error', duration: 3000 });
            }
        } finally {
            setSavingId(null);
        }
    };

    // AI-enabled tenants first, then by billable orders desc.
    const sorted = [...tenants].sort((a, b) => {
        if (a.aiEnabled !== b.aiEnabled) return a.aiEnabled ? -1 : 1;
        return (b.orderCount || 0) - (a.orderCount || 0);
    });

    return (
        <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <HStack>
                    <Text fontWeight="600" color="gray.700">AI photo counting for</Text>
                    <Input
                        type="month"
                        size="sm"
                        maxW="170px"
                        value={month}
                        onChange={(e) => setMonth(e.target.value || currentMonth())}
                    />
                    <Badge colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="md">
                        {grandOrders} AI orders
                    </Badge>
                    <Badge colorScheme={grandCharge > 0 ? 'red' : 'green'} fontSize="sm" px={2} py={1} borderRadius="md">
                        ${grandCharge.toFixed(2)} billable
                    </Badge>
                </HStack>
                <IconButton icon={<FiRefreshCw />} size="sm" variant="ghost" onClick={fetchUsage} aria-label="Refresh" />
            </Flex>

            <Text fontSize="xs" color="gray.500">
                AI counting is opt-in per tenant (off by default). When on, photos are analyzed
                by Claude Vision and the tenant is billed once per order that used AI (deduped
                across weight, intake, and fold). When off, tenants still upload photos and
                advance orders — no AI, no charge.
            </Text>

            {loading ? (
                <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : sorted.length === 0 ? (
                <Box textAlign="center" py={10} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100">
                    <Text color="gray.400">No tenants found.</Text>
                </Box>
            ) : (
                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Tenant</Th>
                                <Th>AI</Th>
                                <Th>Rate / order</Th>
                                <Th isNumeric>AI orders</Th>
                                <Th isNumeric>Analyses</Th>
                                <Th isNumeric>Charge</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {sorted.map((t) => {
                                const draft = rateDrafts[t.laundryId];
                                const rateStr = draft !== undefined ? draft : (t.orderRate ?? 0).toFixed(2);
                                const rateDirty = draft !== undefined && parseFloat(draft) !== t.orderRate;
                                return (
                                    <Tr key={t.laundryId}>
                                        <Td>
                                            <Text fontWeight="500">{t.laundryName || t.laundryId}</Text>
                                            <Text fontSize="10px" color="gray.400" fontFamily="mono">{t.laundryId}</Text>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Switch
                                                    isChecked={!!t.aiEnabled}
                                                    isDisabled={savingId === t.laundryId}
                                                    colorScheme="purple"
                                                    onChange={(e) => saveSettings(t.laundryId, { aiEnabled: e.target.checked })}
                                                />
                                                <Badge colorScheme={t.aiEnabled ? 'purple' : 'gray'} fontSize="9px">
                                                    {t.aiEnabled ? 'On' : 'Off'}
                                                </Badge>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <HStack spacing={1}>
                                                <Text fontSize="xs" color="gray.400">$</Text>
                                                <Input
                                                    size="xs"
                                                    maxW="72px"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="1"
                                                    value={rateStr}
                                                    onChange={(e) => setRateDrafts((prev) => ({ ...prev, [t.laundryId]: e.target.value }))}
                                                />
                                                {rateDirty && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="purple"
                                                        isLoading={savingId === t.laundryId}
                                                        onClick={() => saveSettings(t.laundryId, { orderRate: parseFloat(draft) })}
                                                    >
                                                        Save
                                                    </Button>
                                                )}
                                            </HStack>
                                        </Td>
                                        <Td isNumeric fontWeight="600" color={t.orderCount > 0 ? 'purple.600' : 'gray.400'}>
                                            {t.orderCount || 0}
                                        </Td>
                                        <Td isNumeric color="gray.500">{t.analysisCount || 0}</Td>
                                        <Td isNumeric color={(t.charge || 0) > 0 ? 'red.600' : 'gray.400'} fontWeight={(t.charge || 0) > 0 ? '600' : '400'}>
                                            ${(t.charge || 0).toFixed(2)}
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </VStack>
    );
}
